import React from 'react';
import { Users, Shield, Lock, Check, X } from 'lucide-react';

/**
 * ContactPermissionDialog - Shows clear explanation of why contacts access is needed
 * Displays before requesting permission to comply with privacy best practices
 */
const ContactPermissionDialog = ({ isOpen, onAccept, onDecline, mode = 'request' }) => {
  if (!isOpen) return null;

  const isFirstTime = mode === 'request';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {isFirstTime ? 'Enable Contact Sync' : 'Contact Access Required'}
              </h2>
              <p className="text-blue-100 text-sm">
                Help us make your experience better
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          {/* Main explanation */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Why we need access to your contacts
            </h3>
            <p className="text-gray-600 leading-relaxed">
              AccToo would like to sync your phone contacts to make it easier for you to add 
              customers, suppliers, and other parties to your accounting records.
            </p>
          </div>

          {/* What we collect */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
              <Check className="w-5 h-5 mr-2" />
              What we collect
            </h4>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Contact name</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Phone number</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Email address (if available)</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>Company/Organization name (if available)</span>
              </li>
            </ul>
          </div>

          {/* Privacy commitments */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-green-600" />
              Your privacy is protected
            </h4>
            
            <div className="space-y-2">
              <div className="flex items-start text-sm text-gray-700">
                <Lock className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                <div>
                  <strong>Secure storage:</strong> All contact data is encrypted and stored securely
                </div>
              </div>
              
              <div className="flex items-start text-sm text-gray-700">
                <Lock className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                <div>
                  <strong>No sharing:</strong> We never sell or share your contacts with third parties
                </div>
              </div>
              
              <div className="flex items-start text-sm text-gray-700">
                <Lock className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                <div>
                  <strong>You control it:</strong> You can disable sync and delete synced contacts anytime
                </div>
              </div>
              
              <div className="flex items-start text-sm text-gray-700">
                <Lock className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                <div>
                  <strong>Limited fields only:</strong> We only access name, phone, and email - nothing else
                </div>
              </div>
            </div>
          </div>

          {/* Optional feature note */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <p className="text-sm text-gray-600">
              <strong>Note:</strong> This feature is completely optional. You can use AccToo 
              without enabling contact sync and manually add parties as needed.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
          <button
            onClick={onDecline}
            className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center"
          >
            <X className="w-4 h-4 mr-2" />
            Not Now
          </button>
          <button
            onClick={onAccept}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center"
          >
            <Check className="w-4 h-4 mr-2" />
            Enable Contact Sync
          </button>
        </div>

        {/* Privacy policy link */}
        <div className="px-6 py-3 bg-gray-100 border-t border-gray-200">
          <p className="text-xs text-gray-600 text-center">
            By enabling contact sync, you agree to our{' '}
            <a 
              href="/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Privacy Policy
            </a>
            {' '}and{' '}
            <a 
              href="/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Terms of Service
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactPermissionDialog;

























































